import {
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NbCardModule,
  NbCheckboxModule,
  NbSpinnerModule,
} from '@nebular/theme';
import { WalkaroundSummaryComponent } from './walkaround-summary.component';
import { WalkaroundCompareComponent } from './walkaround-compare.component';
import { WalkaroundApiService } from '../services/walkaroundApiService.service';
import {
  EquipmentWalkaroundRow,
  WalkaroundRecord,
  normalizeDamages,
  recordFromRow,
} from './walkaround.utils';

/**
 * Walk-around history for one piece of equipment. Pick one to view it, or two
 * to compare them (older on the left).
 */
@Component({
  selector: 'stack-mono-equipment-walkarounds',
  templateUrl: './equipment-walkarounds.component.html',
  styleUrls: ['./equipment-walkarounds.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    NbCardModule,
    NbCheckboxModule,
    NbSpinnerModule,
    WalkaroundSummaryComponent,
    WalkaroundCompareComponent,
  ],
})
export class EquipmentWalkaroundsComponent implements OnChanges {
  private readonly api = inject(WalkaroundApiService);
  private readonly destroyRef = inject(DestroyRef);

  equipmentId = input<string | null | undefined>(null);
  take = input(20);

  rows = signal<EquipmentWalkaroundRow[]>([]);
  loading = signal(false);
  error = signal(false);
  selectedIds = signal<string[]>([]);

  private records = computed(() => {
    const map = new Map<string, WalkaroundRecord>();
    for (const row of this.rows()) {
      const record = recordFromRow(row);
      if (record) map.set(row.id, record);
    }
    return map;
  });

  /** Selected records, oldest first. */
  selected = computed<WalkaroundRecord[]>(() =>
    this.selectedIds()
      .map((id) => this.records().get(id))
      .filter((r): r is WalkaroundRecord => !!r)
      .sort(
        (a, b) =>
          new Date(a.capturedAt ?? 0).getTime() -
          new Date(b.capturedAt ?? 0).getTime(),
      ),
  );

  ngOnChanges(changes: SimpleChanges) {
    if (changes['equipmentId'] || changes['take']) this.load();
  }

  load() {
    const equipmentId = this.equipmentId();
    this.selectedIds.set([]);
    if (!equipmentId) {
      this.rows.set([]);
      return;
    }
    this.loading.set(true);
    this.error.set(false);
    this.api
      .equipmentWalkarounds(equipmentId, this.take())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.rows.set(rows || []);
          // Open the latest one so the tab is not empty on arrival.
          if (rows?.length) this.selectedIds.set([rows[0].id]);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  damageCount(row: EquipmentWalkaroundRow): number {
    return normalizeDamages(row.damages).length;
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  /** Keeps at most two selected; a third pick replaces the oldest pick. */
  toggle(id: string) {
    const current = this.selectedIds();
    if (current.includes(id)) {
      this.selectedIds.set(current.filter((x) => x !== id));
    } else {
      this.selectedIds.set([...current, id].slice(-2));
    }
  }

  /**
   * Enter/Space on the focused row, like a click. Only for the row itself:
   * keys on the checkbox inside already toggle it via checkedChange, and
   * acting on their bubbled event too would toggle twice.
   */
  onRowKeydown(event: KeyboardEvent, id: string) {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggle(id);
  }
}
